// Utility function for exact length validation
export const validateLength = (fieldName, exactLength) => {
  return (_, value) => {
    if (value && value.length !== exactLength) {
      return Promise.reject(new Error(`${fieldName} must be exactly ${exactLength} characters long`));
    }
    return Promise.resolve();
  };
};

export const validateAllowedLengths = (fieldName, allowedLengths) => {
  return (_, value) => {
    if (!value) return Promise.resolve();

    if (!allowedLengths.includes(value.length)) {
      return Promise.reject(
        new Error(
          `${fieldName} must be ${allowedLengths.join(', ')} digits long`
        )
      );
    }

    return Promise.resolve();
  };
};

// Utility function to validate non-zero values
export const validateNonZero = (fieldName) => {
  return (_, value) => {
    if (value && /^0+$/.test(value)) {
      return Promise.reject(new Error(`${fieldName} cannot be all zeros`));
    }
    return Promise.resolve();
  };
};

// Utility function to validate GS1 check digit
export const validateGS1CheckDigit = (fieldName) => {
  const allowedLengths = [8, 12, 13, 14];

  return (_, value) => {
    if (!value) return Promise.resolve();

    if (!allowedLengths.includes(value.length)) {
      return Promise.resolve(); // let length validator handle it
    }

    const digits = value.split('').map(Number);
    const checkDigit = digits.pop();

    // GS1: always calculate from RIGHT
    const sum = digits
      .reverse()
      .reduce((acc, digit, index) => {
        const weight = index % 2 === 0 ? 3 : 1;
        return acc + digit * weight;
      }, 0);

    const calculatedCheckDigit = (10 - (sum % 10)) % 10;

    if (checkDigit !== calculatedCheckDigit) {
      return Promise.reject(
        new Error(`${fieldName} has an invalid GS1 check digit`)
      );
    }

    return Promise.resolve();
  };
};

export const validateGS1 = (fieldName, type) => {
  return (_, value) => {
    if (!value) return Promise.resolve();

    const config = {
      GTIN8: { length: 8 },
      UPC11: { length: 11 }, // custom
      UPC12: { length: 12 }, // UPC-A
      GTIN13: { length: 13 },
    };

    const { length } = config[type] || {};

    if (!length || value.length !== length) {
      return Promise.resolve(); // let length validator handle it
    }

    const digits = value.split('').map(Number);
    const checkDigit = digits.pop();

    // Always apply weights from RIGHT
    const sum = digits
      .reverse()
      .reduce((acc, digit, index) => {
        const weight = index % 2 === 0 ? 3 : 1;
        return acc + digit * weight;
      }, 0);

    const calculated = (10 - (sum % 10)) % 10;

    if (calculated !== checkDigit) {
      return Promise.reject(
        new Error(`${fieldName} has an invalid check digit`)
      );
    }

    return Promise.resolve();
  };
};

export const validateUPC11CheckDigit = (fieldName) => {
  return (_, value) => {
    if (!value) return Promise.resolve();

    if (value.length !== 11) return Promise.resolve(); // let length validator handle it

    const digits = value.split('').map(Number);
    const checkDigit = digits.pop(); // last digit

    const sum = digits
      .reverse()
      .reduce((acc, digit, index) => {
        const weight = index % 2 === 0 ? 3 : 1;
        return acc + digit * weight;
      }, 0);

    const calculated = (10 - (sum % 10)) % 10;

    if (calculated !== checkDigit) {
      return Promise.reject(
        new Error(`${fieldName} has an invalid check digit`)
      );
    }

    return Promise.resolve();
  };
};

export const generateUPCCheckDigit = (value) => {
  const digits = value.split('').map(Number).reverse();

  const sum = digits.reduce((acc, digit, index) => {
    const weight = index % 2 === 0 ? 3 : 1;
    return acc + digit * weight;
  }, 0);

  return (10 - (sum % 10)) % 10;
};